import React from "react";
import { AlertTriangle } from "lucide-react";

/**
 * Purpose: catch runtime render errors anywhere in the page tree so a broken
 * page shows a friendly message instead of a blank white screen. React error
 * boundaries must be class components.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-[60vh] grid place-items-center px-6 text-center" data-testid="error-boundary">
          <div className="max-w-md">
            <span className="mx-auto mb-5 grid h-16 w-16 place-items-center rounded-2xl bg-gradient-to-br from-destructive/15 to-destructive/5 ring-1 ring-destructive/10 text-destructive animate-float-soft">
              <AlertTriangle className="h-8 w-8" />
            </span>
            <h1 className="font-display text-3xl font-black tracking-tight text-primary">Something went wrong</h1>
            <p className="text-muted-foreground mt-3">
              This page hit an unexpected error. Please try again, or head back to the homepage.
            </p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="inline-flex items-center justify-center rounded-md border border-border bg-background px-4 py-2 text-sm font-bold hover:bg-muted transition"
              >
                Try again
              </button>
              <button
                type="button"
                onClick={() => window.location.assign("/")}
                className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-bold text-primary-foreground shadow hover:opacity-90 transition"
              >
                Back to home
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
